import dayjs from "dayjs";
import { useState } from "react";

import { BottomSlider } from "@components/BottomSlider";
import { ErrorComponent } from "@components/ErrorComponent";
import { DateInput } from "@components/Input";
import { Loader } from "@components/Loader";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { DateFormat } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import {
  useCreateTermsConsent,
  useErrorBanner,
  useOnboarding,
  useSetUserIdentity,
  useUser,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import { TermsAndConditions } from "./TermsAndConditions";

const MINIMUM_DATE = dayjs().subtract(120, "years").toDate();
const MAXIMUM_DATE = dayjs().subtract(18, "years").toDate();

export const DateOfBirthRoute = () => {
  const [loading, setLoading] = useState(false);
  const [birthDate, setBirthDate] = useState<Date>();

  const [setUserIdentity] = useSetUserIdentity();
  const [createTermsConsent] = useCreateTermsConsent();

  const { resetToNext } = useOnboarding();

  const { loading: userLoading, error: userError } = useUser();

  const { showErrorBanner } = useErrorBanner();

  const handleSubmit = async () => {
    const dateOfBirth = dayjs(birthDate).format(DateFormat.User);

    try {
      setLoading(true);

      await setUserIdentity({
        variables: {
          dateOfBirth,
        },
      });

      await createTermsConsent({
        variables: {
          viewedTimestamp: new Date(),
        },
      });

      // Carrier prefill already ran right after phone verification; landing
      // here means it missed, so continue collecting fields manually.
      resetToNext();
    } catch {
      showErrorBanner();
    } finally {
      setLoading(false);
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.DateOfBirthRouteTitle),
    cta: {
      primary: {
        title: i18n.t(Translations.DateOfBirthRoutePrimaryCTA),
        onClick: handleSubmit,
        disabled: !birthDate,
        loading,
      },
    },
  };

  if (userLoading) return <Loader />;
  if (userError) return <ErrorComponent />;

  return (
    <OnboardingLayout {...props}>
      <DateInput
        label={i18n.t(Translations.DateOfBirthRouteInputLabel)}
        value={birthDate}
        onChange={setBirthDate}
        minimumDate={MINIMUM_DATE}
        maximumDate={MAXIMUM_DATE}
      />
      <Spacing size={SpacingSizes.M} />

      <BottomSlider>
        <TermsAndConditions />
      </BottomSlider>
    </OnboardingLayout>
  );
};
